import { Link } from "react-router-dom";
import {
  Anchor,
  ArrowRight,
  BadgeCheck,
  Briefcase,
  ClipboardCheck,
  Eye,
  HeartHandshake,
  Target,
  TrendingUp,
} from "lucide-react";
import { PageHero } from "@/components/layout/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { DEFAULT_HERO_IMAGE } from "@/lib/icons";

const CORE_VALUES = [
  { icon: BadgeCheck, title: "Integrity", desc: "We do what we say, and we say what we do — transparently." },
  { icon: Briefcase, title: "Professionalism", desc: "Trained teams, clear processes, and polished delivery." },
  { icon: Anchor, title: "Reliability", desc: "Consistent service our clients can plan their operations around." },
  { icon: HeartHandshake, title: "Customer Focus", desc: "Solutions shaped around each client's real requirements." },
  { icon: ClipboardCheck, title: "Accountability", desc: "One partner, one standard, clear ownership of outcomes." },
  { icon: TrendingUp, title: "Continuous Improvement", desc: "We review, refine, and raise our standards constantly." },
];

const AboutPage = () => {
  const { content } = useSite();
  const storyParagraphs = (content?.story || "").split("\n\n").filter(Boolean);

  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About SSS GROUPS"
        title="The Partner Behind Seamless Operations"
        description="SIREE SOLUTIONS GROUPS brings nine professional service verticals together — so businesses, institutions, and property owners get one trusted team instead of nine vendors."
        image={content?.about_image_2}
      />

      {/* Overview */}
      <section className="bg-white py-24" data-testid="about-overview">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <div className="gold-frame">
              <img
                src={content?.about_image_1 || DEFAULT_HERO_IMAGE}
                alt="SSS GROUPS leadership and operations team"
                className="w-full rounded-2xl object-cover shadow-2xl"
                loading="lazy"
              />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-gold-dark">
              Company Overview
            </p>
            <h2 className="font-serif text-3xl font-bold leading-tight text-navy-900 sm:text-4xl">
              One Organization. Nine Disciplines. Zero Vendor Chaos.
            </h2>
            <p className="mt-6 leading-relaxed text-slate-600">{content?.about_short}</p>
            <p className="mt-4 leading-relaxed text-slate-600">
              Whether you manage a hospital, a corporate office, a residential community, or a flagship event,
              SSS GROUPS gives you a single accountable partner for the services that keep your world running.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-slate-100 py-24" data-testid="about-mission-vision">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 md:grid-cols-2 lg:px-10">
          <Reveal>
            <div className="grain-overlay relative h-full overflow-hidden rounded-2xl bg-navy-900 p-10">
              <Target className="h-10 w-10 text-gold" />
              <h3 className="mt-6 font-serif text-2xl font-bold text-white">Our Mission</h3>
              <p className="mt-4 leading-relaxed text-slate-300">
                To deliver reliable, professional, and customer-focused solutions that simplify operations and
                create value for businesses and individuals.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full rounded-2xl border-2 border-gold/60 bg-white p-10">
              <Eye className="h-10 w-10 text-gold-dark" />
              <h3 className="mt-6 font-serif text-2xl font-bold text-navy-900">Our Vision</h3>
              <p className="mt-4 leading-relaxed text-slate-600">
                To become a trusted multi-service solutions partner recognized for professionalism, service
                quality, and long-term customer relationships.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Story */}
      <section id="story" className="scroll-mt-24 bg-white py-24" data-testid="about-story">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <SectionHeading eyebrow="Our Story" title="How SSS GROUPS Came to Be" />
          <Reveal>
            <div className="space-y-6">
              {storyParagraphs.map((p, i) => (
                <p
                  key={i}
                  className={`leading-relaxed ${
                    i === 0
                      ? "font-serif text-xl italic leading-relaxed text-navy-800 sm:text-2xl"
                      : "text-slate-600"
                  }`}
                >
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Core values */}
      <section className="grain-overlay relative bg-navy-900 py-24" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading eyebrow="What Guides Us" title="Our Core Values" dark />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CORE_VALUES.map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={(i % 3) * 0.08}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50">
                  <Icon className="h-8 w-8 text-gold" />
                  <h3 className="mt-5 text-lg font-bold text-white">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Service commitment */}
      <section className="bg-slate-100 py-24" data-testid="about-commitment">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-10">
          <Reveal>
            <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-gold-dark">
              Our Service Commitment
            </p>
            <p className="font-serif text-2xl italic leading-relaxed text-navy-900 sm:text-3xl">
              "Every client, every site, every shift — we show up prepared, we deliver with care, and we stay
              accountable long after the work is done."
            </p>
            <p className="mt-6 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-gold-dark" data-testid="about-tagline">
              — Together for a Better Tomorrow —
            </p>
            <span className="mx-auto mt-8 block h-px w-16 bg-gold" />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="grain-overlay relative bg-navy-950 py-20" data-testid="about-cta">
        <Reveal className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-3xl font-bold text-white sm:text-4xl">
            Ready to Work With a <span className="gold-text-gradient italic">Partner You Can Trust?</span>
          </h2>
          <p className="mt-4 text-slate-300">Tell us what you need — we will build the right solution around it.</p>
          <Link
            to="/contact?quote=1"
            data-testid="about-quote-btn"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-navy-950 transition-all duration-300 hover:bg-gold-light"
          >
            Request a Quote
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default AboutPage;
